import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { api, ApiRequestError, type SessionResponse } from './api.ts';
import { applyTheme, readStoredTheme } from './theme.ts';

interface SessionState {
  status: 'loading' | 'signed-out' | 'signed-in';
  session: SessionResponse | null;
  theme: string;
  signIn: (code: string) => Promise<void>;
  recoverAsAdmin: (password: string) => Promise<void>;
  signInAsGuest: (token: string) => Promise<void>;
  signOut: () => Promise<void>;
  completeProfile: (input: { displayName: string; avatarValue: string }) => Promise<void>;
  /** Re-reads the session from the server, after a change made elsewhere. */
  refresh: () => Promise<void>;
  changeTheme: (theme: string) => void;
}

const SessionContext = createContext<SessionState | null>(null);

export function SessionProvider({ children }: { children: ReactNode }) {
  const [status, setStatus] = useState<SessionState['status']>('loading');
  const [session, setSession] = useState<SessionResponse | null>(null);
  const [theme, setTheme] = useState<string>(() => readStoredTheme());

  // Apply the locally remembered theme before the network call resolves, so a
  // reload does not flash the default theme at someone mid-game.
  useEffect(() => {
    applyTheme(theme);
  }, [theme]);

  const adopt = useCallback((next: SessionResponse) => {
    setSession(next);
    setStatus('signed-in');
    setTheme(next.theme);
  }, []);

  useEffect(() => {
    let cancelled = false;

    api
      .me()
      .then((next) => {
        if (!cancelled) adopt(next);
      })
      .catch((error: unknown) => {
        if (cancelled) return;
        // A 401 is the normal signed-out case, not a failure worth surfacing.
        if (!(error instanceof ApiRequestError) || error.status !== 401) {
          console.error('Could not load session', error);
        }
        setStatus('signed-out');
      });

    return () => {
      cancelled = true;
    };
  }, [adopt]);

  const signIn = useCallback(
    async (code: string) => {
      adopt(await api.redeem(code));
    },
    [adopt],
  );

  const recoverAsAdmin = useCallback(
    async (password: string) => {
      adopt(await api.adminRecover(password));
    },
    [adopt],
  );

  const signInAsGuest = useCallback(
    async (token: string) => {
      adopt(await api.redeemGuest(token));
    },
    [adopt],
  );

  const signOut = useCallback(async () => {
    await api.logout();
    setSession(null);
    setStatus('signed-out');
  }, []);

  const completeProfile = useCallback(
    async (input: { displayName: string; avatarValue: string }) => {
      adopt(await api.saveProfile({ ...input, theme }));
    },
    [adopt, theme],
  );

  const changeTheme = useCallback(
    (next: string) => {
      setTheme(next);
      // Persisting is best-effort: the theme already applied locally, and a
      // failed write should not throw an error at someone for picking a colour.
      if (session) {
        api.saveTheme(next).catch(() => undefined);
      }
    },
    [session],
  );

  // Used after something outside this provider changed the account — an avatar
  // upload, say — so every screen showing the person updates at once rather
  // than each keeping its own copy in step by hand.
  const refresh = useCallback(async () => {
    const next = await api.me();
    adopt(next);
  }, [adopt]);

  const value = useMemo<SessionState>(
    () => ({
      status,
      session,
      theme,
      signIn,
      recoverAsAdmin,
      signInAsGuest,
      signOut,
      completeProfile,
      refresh,
      changeTheme,
    }),
    [status, session, theme, signIn, recoverAsAdmin, signInAsGuest, signOut, completeProfile, refresh, changeTheme],
  );

  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
}

export function useSession(): SessionState {
  const context = useContext(SessionContext);
  if (!context) throw new Error('useSession must be used inside a SessionProvider');
  return context;
}
